/**
 * useAIDraftForm — shared AI-draft state for module create pages.
 *
 * Encapsulates the pattern the PTW page established: apply a draft through
 * the module's mapper (fill-empty-only), track which AI-filled fields the
 * user has not yet edited (drives the MANDATORY warning banner + input
 * highlights), consume a chat handoff on mount, and build the submit-time
 * feedback beacon.
 *
 *   const ai = useAIDraftForm({
 *     module: 'hira', mapper, formData, setFormData,
 *     getContext: () => ({ departments }), enabled: !isEditMode,
 *   });
 *   <AIPrefillPanel module="hira" onDraft={ai.applyAIDraft} />
 *   <AIDraftWarningBanner aiFieldCount={ai.aiFields.size} ... />
 *   onSubmitSuccess: ai.sendFeedback(formData)
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { sendPrefillFeedback } from '../services/assistantClient';
import { consumeDraft } from '../utils/aiPrefill/handoff';

export default function useAIDraftForm({
  module,
  mapper,               // { applyDraft(draft, formData, ctx) }
  formData,
  setFormData,
  getContext = () => ({}),
  enabled = true,
}) {
  const [aiFields, setAiFields] = useState(new Set());
  const [aiMeta, setAiMeta] = useState(null);
  // Mandatory pre-submit acknowledgement: pages must block submit while
  // requiresAck && !aiVerifiedAck. Reset on every new draft application.
  const [aiVerifiedAck, setAiVerifiedAck] = useState(false);
  const contextRef = useRef(getContext);
  contextRef.current = getContext;

  // Report computed from the current snapshot; the actual merge re-runs in a
  // functional update (React defers updater functions — their side effects
  // cannot be read synchronously). mapper.applyDraft is pure.
  const applyAIDraft = (result) => {
    const draft = result.draft || {};
    const ctx = contextRef.current();
    const report = mapper.applyDraft(draft, formData, ctx);
    setFormData((prev) => mapper.applyDraft(draft, prev, ctx).formData);
    const appliedValues = {};
    report.applied.forEach((f) => { appliedValues[f.path] = f.value; });
    setAiFields(new Set(report.applied.map((f) => f.path)));
    setAiMeta({
      generationId: result.generation_id || null,
      applied: appliedValues,
      appliedLabels: Object.fromEntries(report.applied.map((f) => [f.path, f.label])),
      certainty: result.certainty || {},
      appliedAt: Date.now(),
    });
    setAiVerifiedAck(false);
    return report;
  };

  // A user edit to an AI-filled field marks it verified.
  useEffect(() => {
    if (!aiMeta || aiFields.size === 0) return;
    const stillAi = new Set();
    aiFields.forEach((path) => {
      if (JSON.stringify(formData[path]) === JSON.stringify(aiMeta.applied[path])) {
        stillAi.add(path);
      }
    });
    if (stillAi.size !== aiFields.size) setAiFields(stillAi);
  }, [formData]); // eslint-disable-line

  // Chat → form handoff: on mount AND on every navigation to this route
  // (location.key changes when the widget CTA fires while already on the
  // page). Consume-on-read keeps re-runs and refreshes idempotent.
  const location = useLocation();
  useEffect(() => {
    if (!enabled) return;
    const payload = consumeDraft(module);
    if (payload) applyAIDraft(payload);
    // eslint-disable-next-line
  }, [location.key]);

  const lowCertaintyFields = aiMeta
    ? [...aiFields]
        .filter((p) => aiMeta.certainty[p] === 'low')
        .map((p) => ({ path: p, label: aiMeta.appliedLabels?.[p] || p }))
    : [];

  const aiClass = (path) => (aiFields.has(path) ? ' ring-2 ring-amber-300 bg-amber-50' : '');

  const sendFeedback = (finalFormData) => {
    if (!aiMeta?.generationId) return;
    const appliedPaths = Object.keys(aiMeta.applied);
    const kept = [];
    const edited = [];
    const cleared = [];
    appliedPaths.forEach((path) => {
      const current = finalFormData[path];
      if (JSON.stringify(current) === JSON.stringify(aiMeta.applied[path])) kept.push(path);
      else if (current === '' || current == null || (Array.isArray(current) && current.length === 0)) cleared.push(path);
      else edited.push(path);
    });
    sendPrefillFeedback({
      generation_id: aiMeta.generationId,
      module,
      fields_applied: appliedPaths,
      fields_kept: kept,
      fields_edited: edited,
      fields_cleared: cleared,
      submitted: true,
    });
  };

  // True once AI populated at least one field this session — submit must then
  // be gated on aiVerifiedAck.
  const requiresAck = !!aiMeta && Object.keys(aiMeta.applied).length > 0;

  return {
    aiFields, aiMeta, applyAIDraft, aiClass, lowCertaintyFields, sendFeedback,
    requiresAck, aiVerifiedAck, setAiVerifiedAck,
  };
}
